import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import { Autocomplete, Button, Collapse, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppRoutes } from '~/app/app.routes';
import { DashboardActivity, DashboardActivityValue, DashboardService } from '~/service/service.dashboard';
import { DashboardActivitiesService } from '~/service/service.dashboard.activities';
import { useService } from '~/shared/service/service.base';
import { useTr } from '~/text';
import { GridToolbarView } from '../grid/grid.toolbar';
import { Col, Row, Text } from '../view.box';
import { dashboardCardNumber } from './dashboard.card';

export type DashboardActivityItemProps = { activity: DashboardActivity };
export const DashboardActivityItem: FC<DashboardActivityItemProps> = observer(({ activity }) => {
	const [svc] = useService(DashboardService);
	const { t } = useTranslation('dashboardActivity');
	const [ta] = useTranslation('dashboardActivityAction');
	const open = svc.openActions.has(activity.key);

	return (
		<Col>
			<Row justifyContent="space-between" gap={2}>
				<Button
					variant="text"
					size="small"
					color="inherit"
					onClick={() => svc.toggleAction(activity.key)}
					fullWidth
					startIcon={open ? <ExpandLessIcon color="inherit" /> : <ReadMoreIcon color="inherit" />}
					endIcon={<Text color="text.secondary">{dashboardCardNumber(activity.count)}</Text>}
				>
					<Text flex={1} noWrap textAlign="left" textTransform="none">
						{t(activity.key)}
					</Text>
				</Button>
			</Row>
			{activity.activities && activity.activities.length > 0 && (
				<Collapse in={open} timeout="auto" unmountOnExit>
					<Col mt={1} gap={1} px={0.5}>
						{activity.activities.map(sub => (
							<Link
								key={sub.key}
								to={AppRoutes.DashboardActivities.buildPath(
									{},
									{ action: sub.action, type: sub.type, value: sub.value },
								)}
							>
								<Row justifyContent="space-between" gap={2} pl={2} flex={1}>
									<Text noWrap textAlign="left" color="text.secondary">
										{ta(sub.value || sub.action || sub.key)}
									</Text>
									<Text color="text.secondary">{dashboardCardNumber(sub.count)}</Text>
								</Row>
							</Link>
						))}
					</Col>
				</Collapse>
			)}
		</Col>
	);
});

export const DashboardActivityType: FC<DashboardActivityItemProps> = ({ activity }) => {
	const t = useTr(['dashboardActivity']);
	return <>{t(activity.type)}</>;
};

export const DashboardActivityAction: FC<DashboardActivityItemProps> = ({ activity }) => {
	const t = useTr(['dashboardActivityAction']);
	return <>{activity.action ? t(activity.action) : 'N/A'}</>;
};

export const DashboardActivitiesToolbar: FC = observer(() => {
	const [dashSvc, svc] = useService(DashboardService, DashboardActivitiesService);
	const tt = useTr(['dashboardActivity']);
	const ta = useTr(['dashboardActivityAction']);

	return (
		<GridToolbarView onReload={svc.reloadList} gridState={svc.gridState} filterable={false}>
			<Row gap={2}>
				<Autocomplete
					size="small"
					value={svc.filterActivityType}
					options={dashSvc.uniqueTypes.sort((a, b) => a.localeCompare(b))}
					getOptionLabel={option => tt(option)}
					isOptionEqualToValue={(option, value) => option === value}
					onChange={svc.setFilterActivityType}
					renderInput={params => <TextField {...params} label="Type" InputProps={params.InputProps} />}
					sx={{ minWidth: 300 }}
				/>
				<Autocomplete
					size="small"
					value={svc.filterActivityAction || null}
					options={dashSvc.uniqueActions}
					getOptionLabel={option => ta(option)}
					isOptionEqualToValue={(option, value) => option === value}
					onChange={svc.setFilterActivityAction}
					renderInput={params => <TextField {...params} label="Action" InputProps={params.InputProps} />}
					sx={{ minWidth: 200 }}
				/>
				<Autocomplete
					freeSolo
					size="small"
					value={svc.filterActivityValue || null}
					options={dashSvc.uniqueValues}
					getOptionLabel={option =>
						dashSvc.uniqueValues.includes(option) ? ta(option as DashboardActivityValue) : option
					}
					isOptionEqualToValue={(option, value) => option === value}
					onChange={svc.setFilterActivityValue}
					renderInput={params => <TextField {...params} label="Value" InputProps={params.InputProps} />}
					sx={{ minWidth: 200 }}
				/>
			</Row>
		</GridToolbarView>
	);
});
